// src/App.tsx

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import PasscodeGate from "@/components/layout/PasscodeGate";
import {
  DashboardPage,
  AudiencesPage,
  CampaignsPage,
  IntelligencePage,
  ReportingPage,
  PartnersPage,
  NotFoundPage,
  ROICalculatorPage,
  BusinessCasePage,
} from "@/pages";
import type { UserRole } from "@/types";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:           1000 * 60 * 5,
      retry:               1,
      refetchOnWindowFocus:false,
    },
  },
});

function ProtectedRoute({
  children,
  roles,
}: {
  children: React.ReactNode;
  roles?: UserRole[];
}) {
  const { isAuthed, can } = useAuth();
  if (!isAuthed) return <Navigate to="/" replace />;
  if (roles && !can(roles)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="reporting" element={<ReportingPage />} />
        <Route path="audiences" element={
          <ProtectedRoute roles={["mrn_admin","retailer_admin","brand_manager","agency_user"]}>
            <AudiencesPage />
          </ProtectedRoute>
        } />
        <Route path="campaigns" element={
          <ProtectedRoute roles={["mrn_admin","brand_manager","brand_analyst","agency_user"]}>
            <CampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="intelligence" element={
          <ProtectedRoute roles={["mrn_admin","retailer_admin","retailer_analyst"]}>
            <IntelligencePage />
          </ProtectedRoute>
        } />
        <Route path="partners" element={
          <ProtectedRoute roles={["mrn_admin","retailer_admin"]}>
            <PartnersPage />
          </ProtectedRoute>
        } />
        <Route path="roi-calculator" element={<ROICalculatorPage />} />
        <Route path="business-case" element={
          <ProtectedRoute roles={["mrn_admin"]}>
            <BusinessCasePage />
          </ProtectedRoute>
        } />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PasscodeGate>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </PasscodeGate>
    </QueryClientProvider>
  );
}